import { Link } from "gatsby";
import React, { useEffect, useState } from "react";
import { formatDate } from "utils/date";
import {
  getLastChangeFromIndexData,
  getLastDateFromIndexData,
  getLastPriceFromIndexData,
} from "utils/indexes";
import { formatPercentage } from "utils/number";

import CloseIcon from "assets/menu-close.inline.svg";
import { getIndexData } from "components/Chart/data";
import { NegativeNumber, NeutralNumber, PositiveNumber } from "styles";

import { IndexData } from "../Chart/utils";
import {
  ExplainerLink,
  ExplainerText,
  HeaderBarContainer,
  HeaderBarWrapper,
  HeaderChange,
  HeaderDivider,
  HeaderPrice,
  IndexText,
} from "./HeaderBar.styles";

type PercentageChangeProps = {
  percentageChange: number;
  priceChange: number;
};

const PercentageChange = ({
  percentageChange,
  priceChange,
}: PercentageChangeProps) => {
  if (percentageChange > 0) {
    return (
      <PositiveNumber $hasDarkBackground>
        +{formatPercentage(priceChange)}
      </PositiveNumber>
    );
  }

  if (percentageChange < 0) {
    return (
      <NegativeNumber $hasDarkBackground>
        {formatPercentage(priceChange)}
      </NegativeNumber>
    );
  }

  return (
    <NeutralNumber>{formatPercentage(priceChange, 0)} Change</NeutralNumber>
  );
};

type HeaderBarProps = {
  onClose?: () => void;
};

export const HeaderBar = ({ onClose }: HeaderBarProps) => {
  const [indexDataWeekly, setIndexDataWeekly] = useState<
    IndexData[] | undefined
  >(undefined);

  useEffect(() => {
    (async () => {
      const { monthlyData, weeklyData } = await getIndexData();
      setIndexDataWeekly(weeklyData);
    })();
  }, []);

  if (indexDataWeekly === undefined) {
    return null;
  }

  const lastIndexUpdate = getLastDateFromIndexData(indexDataWeekly);

  const lastUsedIndexPrice = getLastPriceFromIndexData(indexDataWeekly);
  const usedIndexPriceChange = getLastChangeFromIndexData(indexDataWeekly);
  const usedIndexPercentageChange = usedIndexPriceChange * 100 - 100;

  const lastEvIndexPrice = getLastPriceFromIndexData(
    indexDataWeekly,
    "emEvIndex",
  );
  const evIndexPriceChange = getLastChangeFromIndexData(
    indexDataWeekly,
    "emEvIndex",
  );
  const evIndexPercentageChange = evIndexPriceChange * 100 - 100;

  return (
    <HeaderBarWrapper>
      <Link to="/solutions/indices/#chart">
        <HeaderBarContainer className="gap-x-4">
          <div className="flex gap-x-2">
            <IndexText>EM Used Vehicle Index</IndexText>
            <HeaderPrice>{lastUsedIndexPrice}</HeaderPrice>
            <HeaderChange>
              <PercentageChange
                percentageChange={usedIndexPercentageChange}
                priceChange={usedIndexPriceChange}
              />
            </HeaderChange>
          </div>
          <HeaderDivider />
          <div className="flex gap-x-2">
            <IndexText>EM EV Index</IndexText>
            <HeaderPrice>{lastEvIndexPrice}</HeaderPrice>
            <HeaderChange>
              <PercentageChange
                percentageChange={evIndexPercentageChange}
                priceChange={evIndexPriceChange}
              />
            </HeaderChange>
          </div>
          <div className="ml-auto mr-8">
            <ExplainerText> Indices updated {formatDate(lastIndexUpdate)} 9:00 AM ET. </ExplainerText>
            <ExplainerLink>View indices</ExplainerLink>
          </div>
        </HeaderBarContainer>
      </Link>
      <CloseIcon onClick={onClose} width="16" height="16" />
    </HeaderBarWrapper>
  );
};
